import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ViewTreeNode, ViewTreeNodeFactory } from './models/view.tree-node';
import { BdGraphService } from 'src/lib/graph/graph.service';
import { BdGraph, BdGraphPropertyValueMap, BdGraphNode, BdGraphStateMap } from 'src/lib/graph/graph.model';
import { CameraModel } from './models/camera.model';
import { plainToClass, classToClass } from 'class-transformer';
import { SynchronizationModel } from '../story/models/synchronization.model';
import { GraphUtils } from 'src/lib/graph/graph-utils';
import { ChapterModel } from '../story/models/chapter.model';
import { calculateLeafStateMap } from 'src/lib/graph/leaf-state';
import { PickModel } from './models/pick.model';
import { HitModel } from './models/hit.model';
import { EventsEnum } from './models/events.enum';
import { ShowObjects } from './models/show-objects.model';
import { PostProcessingEffect } from './models/post-processing/effects';
import { TransparencyModeEnum } from './models/post-processing/transparency-mode.enum';
import { BoundaryModel } from './models/boundary.model';
import { EffectsEnum } from './models/post-processing/effects.enum';
import { GroundShadowModel } from './models/post-processing/effects/ground-shadow-model';
import { isNil } from 'lodash';

declare const Human: any;

const graphIdKey = 'objectId';
const graphChildKey = 'objects';

const graphProperties = {
  shown: 'shown',
  renderable: (id) => Human.scene.objects[id].isRenderable(),
  pickable: 'pickable',
  label: (id) => Human.scene.objects[id].displayName,
  expanded: (id) => false,
  layer: 'layer'
};

const graphPropertySync = {
  shown: 'trueWins',
  renderable: 'falseWins',
  pickable: 'trueWins'
};

const defaultGraphConfig = {
  trim: true
};

const enum queryPropertyEnum {
  Hidden = 'hidden',
  Clipped = 'clipped',
  Disabled = 'disabled'
}

class NodeConfig {
  id: string;
  displayName: string;
}

/*
Data specification for per-chapter modifcations to scene graph to create a user facing view graph
*/
class ChapterViewGraphConfig {
  trim: boolean;
  terminal: string[];
  root: {
    children: string[],
    replace: boolean
  };
  nodes: NodeConfig[];
  connect: {
    [key: string]: {
      replace: boolean;
      children: string[];
    };
  };

  constructor(graphData: any) {
    this.trim = graphData.trim || false;
    this.terminal = graphData.terminal || [];
    this.root = graphData.root || {children: [], replace: false};
    this.connect = graphData.connect || {};
    this.nodes = graphData.nodes || [];
  }
}

@Injectable({
  providedIn: 'root'
})
export class EngineService {

  // #TODO: [EM] these maps should be in state
  private objectToNodeMap: {[key: string]: string} = {}; // one object id in the engine maps to one node in the view graph
  private nodeToObjectMap: {[key: string]: string[]} = {}; // one node in the view graph maps to one or more engine object ids.

  /**
   * Maps Human.events from the engine in application observables so that we can subscribe, filter, buffer
   * and more without needing direct knowlege of the Human library across the application.
   */
  private events: any = {};

  private makeObservable(event: EventsEnum) {
    return new Observable(observer => {
      Human.events.on(event, (params) => {
        observer.next(params);
      });
    });
  }

  constructor(
    private graphService: BdGraphService
  ) {
    this.events = Object.values(EventsEnum)
      .reduce((result, event) => {
        result[event] = this.makeObservable(event);
        return result;
      }, {});
  }

  getCanvas(): HTMLCanvasElement {
    return Human.renderer.canvas.canvas;
  }

  getEvent(event: EventsEnum): Observable<any> {
    return this.events[event];
  }

  fireEvent(event: EventsEnum, params: any): void {
    Human.events.fire(event, params);
  }

  getSceneBoundary(): BoundaryModel {
    return Human.scene.getBoundary();
  }

  getObjectBoundary(objectId: string): BoundaryModel {
    return Human.scene.objects[objectId].getBoundary();
  }

  // gets if the object's geometry has been loaded
  getObjectLoaded(objectId: string): boolean {
    const boundary = this.getObjectBoundary(objectId);
    if (isNil(boundary)) {
      return false;
    } else {
      return Object.values(boundary)
        .every((prop) => isFinite(prop));
    }
  }

  pickScene(pick: PickModel): HitModel {
    return Human.renderer.pick.pick(
      pick.x,
      pick.y,
      { rayPick: pick.rayPick }
    );
  }

  transformObject(objectId: string, params: any) {
    const object = Human.scene.objects[objectId];
    if (object) {
        object.setTransform(params);
    } else {
      console.error(`Scene object not found: '${objectId}'`);
    }
  }

  // set 'shown' attribute of specified nodes to specifed value and synchronize viewGraph
  toggleNodes(nodeIds: string[], visible: boolean) {
    // handles the conversion of view graph nodes to engine objects

    // Bind fetchAssets to visible, so that any time a node is set to visible the engine
    // will attempt to load it's assets (if they haven't been loaded already)
    const config = {replace: false, objects: {}, fetchAssets: visible };

    nodeIds.forEach((nodeId) => {
        this.getObjects(nodeId).forEach((humanObjectId: string) => {
          config.objects[humanObjectId] = visible;
        });
    });
    Human.scene.setEnabledObjects(config);
  }


  // Unlike `toggleNodes`, this method doesn't assume that you wish to set a batch of nodes to the same value.
  setNodesVisible(nodes: {[objectId: string]: boolean}, replace = false) {
    const config = {
      replace,
      objects: {...nodes},
      fetchAssets: true
    };
    Human.scene.setEnabledObjects(config);
  }

  /**
  * Sets the specified nodes pickable state (including leaves)
  */
  setNodesPickable(nodes: Record<string, boolean>) {
    // #TODO[AC]? The engine doesn't emit event of pick changes, track via state map

    Object.keys(nodes).forEach((nodeId) => {
      const object = Human.scene.objects[nodeId];
      // set node pickable, else leaves
      const pickableNodeIds = object.isLeaf() ? [nodeId] : this.getLeafObjects(nodeId);
      pickableNodeIds.forEach((pickableNodeId) => {
        const pickableObject = Human.scene.objects[pickableNodeId];
        pickableObject.setPickable(nodes[nodeId]);
      });
    });
  }

  filterObjects(objects, query) {
    let i = objects.length;
    while (i--) {
      switch (true) {
        case objects[i] instanceof Human.scene.Group:
          objects[i].objects = this.filterObjects(objects[i].objects, query);
          if (objects[i].objects.length === 0) {
            objects.splice(i, 1);
          }
          break;

        case objects[i] instanceof Human.scene.Object:
          if (objects[i].displayName.toLowerCase().indexOf(query) === -1) {
            objects.splice(i, 1);
          }
          break;
      }
    }

    return objects;
  }

  setObjectLayer(nodeId: string, layer: number) {
    const object = Human.scene.objects[nodeId];

    object.layer = layer;

    if (object.renderables) {

      for (let i = 0; i < object.renderables.length; ++i) {
        object.renderables[i].setLayer(layer);
      }

    } else if (object.hasOwnProperty('_layerNode')) {

      object._layerNode.setPriority(layer);
    } else if (object.hasOwnProperty('_propertiesNode')) {

      object._propertiesNode.setProperties({ layer: layer });
    }
  }

  getObjectLayerValues(nodeIds: string[]) {
    const objectLayerValues = {};
    const sceneBB = Human.scene.getBoundary();

    nodeIds.forEach((nodeId) => {
      const object = Human.scene.objects[nodeId];
      const bb = object.getBoundary();

      const minX = Math.min(bb.xmin - sceneBB.xmin, sceneBB.xmax - bb.xmax);
      const minY = 2 * Math.min(bb.ymin - sceneBB.ymin, sceneBB.ymax - bb.ymax);
      const minZ = Math.min(bb.zmin - sceneBB.zmin, sceneBB.zmax - bb.zmax);

      const layer = object.layer;
      objectLayerValues[layer] = objectLayerValues[layer] || [];

      objectLayerValues[layer].push({
        nodeId: nodeId,
        value: Math.min(minX, minY, minZ)
      });
    });

    return objectLayerValues;
  }

  getSceneGraph(rootObjects) {
    const sceneGraph = GraphUtils.createGraphFromSrc(rootObjects, graphIdKey, graphChildKey, graphProperties);
    this.graphService.synchronize(sceneGraph, graphPropertySync); // MUTATIVE
    return sceneGraph;
  }

  // build a BdGraph object representing the engine's scene
  createViewGraph(chapterShowObjects: ShowObjects, shouldPrune: boolean = true): BdGraph<ViewTreeNode> {

    const rootObjects: GraphUtils.SGNode[] = shouldPrune
      ? GraphUtils.pruneHiddenObjects(Human.scene.rootObjects, chapterShowObjects)
      : Human.scene.rootObjects;

    // We assume that all content has a root node
    // we must lift that root's child(ren) to be the new 'roots' of the view graph
    // to do this, we can assume that all roots that include the suffix '-root' have showObjects = false,
    // with their immediate children having showObjects = true
    // we can prune again, and any root's children will be pulled to the top of the view graph.

    const formatShowObjects = rootObjects.reduce<ShowObjects>
      ((acc, root) => {
        const turnOff = root.objectId.includes('-root');
        acc[root.objectId] = turnOff === true ? false : true;

        // turn this root's immediate children on
        if (turnOff) {
          root.objects.forEach(child => {
            acc[child.objectId] = true;
          });
        }

        return acc;
      },
    {});

    const viewGraph = this.getSceneGraph(
      GraphUtils.pruneHiddenObjects(rootObjects, formatShowObjects)
    ) as BdGraph<ViewTreeNode>;

    this.graphService.resetDepth(viewGraph);
    return viewGraph;
  }

  // translate engine's object id to the representative view node
  getNode(viewGraph: BdGraph<ViewTreeNode>, id: string): ViewTreeNode {
    if (this.graphService.nodeExists(viewGraph, id)) {
      // object exists in view graph
      return GraphUtils.getNode(viewGraph, id);
    } else if (this.objectToNodeMap[id]) {
      // object is masked
      return GraphUtils.getNode(viewGraph, this.objectToNodeMap[id]) as ViewTreeNode;
    } else {
      // no available node
      return null;
    }
  }

  // #TODO[EM]: this should be in state, harvested from chapter object on load
  // getChapterData
  getChapterGraphConfig(): ChapterViewGraphConfig {
    const c = Human.timeline.activeRoot._nowBranch;
    return new ChapterViewGraphConfig({...defaultGraphConfig, ...c.graph || {}});
  }

  getRootObjects(): any {
    // TODO.. type this
    return Human.scene.rootObjects;
  }

  getAllSceneObjects(): any {
    return Human.scene.objects;
  }

  // given a view node id, return the corresponding human object-id
  getObjects(id: string): string[] {
    if (this.nodeToObjectMap[id]) {
      return this.nodeToObjectMap[id];
    } else {
      return [Human.scene.objects[id].objectId] || [];
    }

  }

  // given a view node id, return the corresponding leaf human-object ids
  // #NOTE: [EM] This is stateful and could benefit from pre-caching viewNodeId -> human leaf objects
  getLeafObjects(id: string): string[] {
    if (id in this.nodeToObjectMap) {
      const leaves = {};
      this.nodeToObjectMap[id].forEach(objectId => {
        if (Human.scene.objects[objectId].isLeaf()) {
          leaves[objectId] = true;
        } else {
          Human.scene.getLeaves(objectId).forEach(leafId => {
            leaves[leafId] = true;
          });
        }
      });
      return Object.keys(leaves);
    } else if (id in Human.scene.objects) {
      return Human.scene.getLeaves(id);
    } else {
      return [];
    }
  }

  getChapter(id: string): any {
    return Human.timeline.branchTimelines[id] || null;
  }

  getActiveChapters(): any[] {
    if (Human.timeline.activeRoot) {
      return Human.timeline.activeRoot._chapters;
    } else {
      return [];
    }
  }

  // return bounding box of objects
  getBoundaryFromObjects(objectIds: string[]): any {
    objectIds = objectIds.filter((x) => Human.scene.objects[x].isLeaf());
    return Human.scene.getBoundary({objectIds: objectIds, clipped: true});
  }

  getLeaves(objectId?: string) {
    return Human.scene.getLeaves(objectId);
  }

  // get visible leaves
  getVisibleLeaves(): string[] {
    return Human.scene.getEnabledLeaves();
  }

  // get camera lookat
  getCameraLookAt(): CameraModel {
    return plainToClass(CameraModel, Human.renderer.camera.getLookAt() as Object);
  }

  // get world position from barycentric coordiantes
  barycentricToWorld(objectId: string, primitiveIndex: number, barycentric: any, renderableIndex: number = 0): [number, number, number] {
    const out = new Float32Array([0, 0, 0 ]);
    const humanObject = Human.scene.objects[objectId];
    humanObject.barycentricToWorldSpace(renderableIndex, primitiveIndex, barycentric, out);
    return !isNaN(out[0]) ? [...Array.from(out)] as [number, number, number] : [0, 0, 0];
  }

  setGraphState(synchronization: SynchronizationModel) {
    Human.scene.setObjectProperties(synchronization);
  }

  // return a list of all states (namespaces) with assets loaded in the engine
  getAssetNamespaces(): string[] {
    const namespaces = {};
    Object.keys(Human.assets.loadedAssets).map(wholeId => wholeId.split('/')[0]).forEach(namespace => {
      namespaces[namespace] = true;
    });
    return Object.keys(namespaces);
  }

  // load assets for graph objects if they exist
  loadGraphObject(id: string): Observable<boolean> {
    const humanObjects = this.getObjects(id).map(objectId => Human.scene.objects[objectId]);

    return new Observable(observer => {
      // load default asset types
      Human.scene.graph.attachAssetSetsToSubtree(humanObjects, null, () => {
        observer.next(true);
        observer.complete();
      });
    });
  }

  setProperties(properties: { [key: string]: any }) {
    Human.properties.set(properties);
  }

  setTransparencyMode(mode: TransparencyModeEnum) {
    Human.renderer.setTransparencyMode(mode);
  }  

  // Post Processing

  setPostProcessingEnabled(enabled: boolean) {
    if (enabled) {
      Human.renderer.postprocess.enable();
    } else {
      Human.renderer.postprocess.disable();
    }
  }

  setPostProcessingFeatures(effects: PostProcessingEffect[]) {
    const ppEffects = effects.filter(
      (effect) => effect.effect !== EffectsEnum.GroundShadow
    );

    Human.renderer.postprocess.parse(ppEffects);

    const groundShadow = effects.find(
      (effect) => effect.effect === EffectsEnum.GroundShadow
    ) as GroundShadowModel;

    if (groundShadow) {
      Human.renderer.groundShadow.init();
      Human.renderer.groundShadow.update({
        blurAmount: groundShadow.blur,
        opacity: groundShadow.opacity,
        translation: [groundShadow.xPos, groundShadow.yPos, groundShadow.zPos],
        scale: groundShadow.size,
      });
    } else {
      Human.renderer.groundShadow.destroy();
    }
  }

  enableAnimation() {
    Human.timeline.enableAnimation();
  }

  enableRender() {
    Human.renderer.enableRender();
  }

  canIRender(): boolean {
    return Human.renderer.canIRender();
  }

}
